import { useState } from "react";
import { Eye, EyeOff, Lock, Mail, Shield, BarChart3, Users, Headphones, Globe } from "lucide-react";
import { encrypt } from "../../config/aes";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { authService } from "../../services/authService";
import useToken from "../../hooks/features/auth/useToken";
import { alertError } from "../../helpers/alertError";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { saveToken, saveIzilifeToken } = useToken();
  const navigate = useNavigate();
  const location = useLocation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const registerData = location.state;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const secureEmail = encrypt(email);
      const securePassword = encrypt(password);
      const response = await authService.login(secureEmail, securePassword);


      if (response?.statusCode === 200 && response?.data?.token && response.data.izilife_token) {
        saveToken(response.data.token);
        saveIzilifeToken(response.data.izilife_token);
        localStorage.setItem("token", response.data.token);
        navigate(`/redirect?token=${response.data.token}&izilife_token=${response.data.izilife_token}`);
      } else {
        alertError(response?.message || "Échec de la connexion. Vérifiez vos identifiants.");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Erreur de connexion. Veuillez réessayer.";
      alert(errorMessage);
      console.log(error?.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F7FC]">
      
      {/* PANNEAU GAUCHE - BRANDING ET TEMOIGNAGE */}
      <div className="hidden lg:flex lg:w-[42%] relative bg-[#031B4E] text-white flex-col justify-between p-12 overflow-hidden">
        {/* Effet de vagues / lignes en arrière-plan simulant la carte et le réseau */}
        <div 
          className="absolute inset-0 opacity-20 pointer-events-none bg-cover bg-center"
          style={{ backgroundImage: "url('/path-to-your-world-map-grid.png')" }} // Optionnel : si tu as le fichier de la carte
        />

        {/* Header Logo */}
        <div className="relative z-10">
          <Link to={"/"} className="flex items-center gap-2">
            <img src="/logo.png" alt="IZI Call Center" className="h-12 w-auto" />
            <div>
              <div className="text-xl font-black tracking-wider leading-none">
                <span className="text-white">Izi</span> <span className="text-[#FFB800]">CALL CENTER</span>
              </div>
              <div className="text-[9px] tracking-[0.25em] text-gray-300 font-medium uppercase mt-0.5">
                Connecter • Assister • Satisfaire
              </div>
            </div>
          </Link>
        </div>

        {/* Content / Features */}
        <div className="relative z-10 my-auto space-y-8 max-w-md">
          <h1 className="text-4xl font-bold leading-tight">
            Connectez-vous <br />
            à une expérience <br />
            client <span className="text-[#FFB800]">exceptionnelle.</span>
          </h1>
          <p className="text-gray-300 text-sm">
            Gérez vos appels, vos équipes et vos clients depuis une plateforme unique et intelligente.
          </p>

          <div className="space-y-6 pt-4">
            {/* Feature 1 */}
            <div className="flex gap-4 items-start">
              <div className="p-2.5 bg-white/10 rounded-full border border-white/20">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Sécurisé et fiable</h4>
                <p className="text-xs text-gray-400 mt-0.5">Vos données sont protégées avec les plus hauts standards de sécurité.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="flex gap-4 items-start">
              <div className="p-2.5 bg-white/10 rounded-full border border-white/20">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Performance en temps réel</h4>
                <p className="text-xs text-gray-400 mt-0.5">Suivez vos performances et prenez les bonnes décisions.</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="flex gap-4 items-start">
              <div className="p-2.5 bg-white/10 rounded-full border border-white/20">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Collaboration simplifiée</h4>
                <p className="text-xs text-gray-400 mt-0.5">Travaillez ensemble et offrez le meilleur service à vos clients.</p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="flex gap-4 items-start">
              <div className="p-2.5 bg-white/10 rounded-full border border-white/20">
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Support 24/7</h4>
                <p className="text-xs text-gray-400 mt-0.5">Notre équipe est disponible à tout moment pour vous accompagner.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Testimonial Card */}
        <div className="relative z-10 bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-md">
          <span className="text-[#FFB800] text-4xl font-serif absolute top-2 left-4">“</span>
          <p className="text-xs text-gray-300 italic pl-6 leading-relaxed">
            IZI Call Center nous a permis d'améliorer notre productivité de 60% et la satisfaction de nos clients.
          </p>
          <div className="flex items-center justify-between mt-4 pl-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-[#FFB800] text-sm">★</span>
              ))}
            </div>
            <span className="text-xs text-gray-400">— Client satisfait</span>
          </div>
        </div>
      </div>

      {/* PANNEAU DROIT - FORMULAIRE DE CONNEXION */}
      <div className="flex-1 flex flex-col justify-between p-6 lg:p-12 relative">
        
        {/* Language Selector */}
        <div className="flex justify-end relative z-10">
          <button className="flex items-center gap-2 border border-gray-200 bg-white px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors">
            <Globe className="w-4 h-4 text-gray-500" />
            <span>Français</span>
            <span className="text-[10px] text-gray-400">▼</span>
          </button>
        </div>

        {/* Central Form Container */}
        <div className="w-full max-w-lg mx-auto my-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-8 lg:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Bienvenue !</h2>
            <p className="text-sm text-gray-500 mt-2">Connectez-vous à votre compte IZI Call Center</p>
          </div>

          <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Input Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-2">
                Adresse e-mail
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="exemple@email.com"
                  className="w-full py-3 pl-12 pr-4 text-sm text-gray-900 border border-gray-200 rounded-xl focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 focus:outline-none transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Input Password */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-xs font-semibold text-gray-700">
                  Mot de passe
                </label>
                <a href="#" className="text-xs font-semibold text-[#0066FF] hover:underline">
                  Mot de passe oublié ?
                </a>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="••••••••••••••••"
                  className="w-full py-3 pl-12 pr-12 text-sm text-gray-900 border border-gray-200 rounded-xl focus:border-[#0066FF] focus:ring-2 focus:ring-[#0066FF]/20 focus:outline-none transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                className="w-4 h-4 text-[#0066FF] border-gray-300 rounded focus:ring-[#0066FF]"
              />
              <label htmlFor="remember-me" className="ml-2.5 text-sm font-medium text-gray-700 select-none">
                Se souvenir de moi
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 text-sm font-semibold text-white bg-[#0066FF] hover:bg-[#0052cc] rounded-xl transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 shadow-sm"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Se connecter</span>
                  <span className="text-base font-light">→</span>
                </>
              )}
            </button>
          </form>

          {/* Separator */}
          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
            <span className="relative bg-white px-3 text-xs text-gray-400 uppercase font-medium">ou</span>
          </div>

          {/* SSO Buttons */}
          <div className="space-y-3">
            {/* Google */}
            <button className="w-full py-2.5 border border-gray-200 rounded-xl flex items-center justify-center gap-3 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M12 5.04c1.64 0 3.12.56 4.28 1.67l3.2-3.2C17.52 1.58 14.92 1 12 1 7.35 1 3.4 3.65 1.5 7.5l3.6 2.8C6.01 7.12 8.79 5.04 12 5.04z"/>
                <path fill="#4285F4" d="M23.5 12.25c0-.82-.07-1.6-.2-2.35H12v4.5h6.48c-.28 1.48-1.12 2.73-2.38 3.58l3.6 2.8c2.1-1.94 3.3-4.8 3.3-8.53z"/>
                <path fill="#FBBC05" d="M5.1 14.7c-.25-.75-.4-1.55-.4-2.35s.15-1.6.4-2.35L1.5 7.2C.54 9.15 0 11.31 0 13.6c0 2.29.54 4.45 1.5 6.4l3.6-2.8z"/>
                <path fill="#34A853" d="M12 23c3.24 0 5.97-1.08 7.96-2.91l-3.6-2.8c-1.1.74-2.5 1.18-4.36 1.18-3.21 0-6-2.08-6.9-4.96l-3.6 2.8C3.4 20.35 7.35 23 12 23z"/>
              </svg>
              <span>Continuer avec Google</span>
            </button>

            {/* Microsoft */}
            <button className="w-full py-2.5 border border-gray-200 rounded-xl flex items-center justify-center gap-3 bg-white hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-700">
              <svg className="w-5 h-5" viewBox="0 0 23 23">
                <path fill="#f35325" d="M0 0h11v11H0z"/>
                <path fill="#81bc06" d="M12 0h11v11H12z"/>
                <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                <path fill="#ffba08" d="M12 12h11v11H12z"/>
              </svg>
              <span>Continuer avec Microsoft</span>
            </button>
          </div>

          {/* Bottom Link */}
          <div className="mt-8 text-center text-sm">
            <span className="text-gray-500">Pas encore de compte ? </span>
            <Link to="/register" className="font-semibold text-[#0066FF] hover:underline">
              Demander une démo
            </Link>
          </div>
        </div>

        {/* Footer info */}
        <div className="text-center text-xs text-gray-400 space-y-2 mt-4">
          <p>© 2024 IZI Call Center. Tous droits réservés.</p>
          <div className="flex justify-center gap-4 text-gray-500">
            <a href="#" className="hover:underline">Conditions d'utilisation</a>
            <span>|</span>
            <a href="#" className="hover:underline">Politique de confidentialité</a>
            <span>|</span>
            <a href="#" className="hover:underline">Support</a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;